import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  POIResponse,
  IPOIFilterParams,
  MediaPayload,
  POI,
  IPOICategory,
  POIMutationResponse,
  POITranslationResponse,
} from '@/types/poi-management.types';

export const POIManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPOIs: builder.query<PaginatedApiResponse<POI[]>, IPOIFilterParams>({
      query: (params) => ({
        url: 'pois',
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedApiResponse<POI[]>) => response,
      providesTags: ['POIs'],
      keepUnusedDataFor: 0,
    }),

    getPOIById: builder.query<POIResponse, string>({
      query: (poi_id) => ({
        url: `pois/${poi_id}`,
        method: 'GET',
      }),
      transformResponse: (response: POIResponse) => response,
      providesTags: (result, error, poi_id) => [{ type: 'POIs', id: poi_id }],
      keepUnusedDataFor: 0,
    }),

    createPOI: builder.mutation<
      POIMutationResponse,
      { data: FormData; harborId?: string | null }
    >({
      query: ({ data, harborId }) => {
        return {
          url: `pois${harborId ? `?harborId=${harborId}` : ''}`,
          method: 'POST',
          data,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
      },
      invalidatesTags: ['POIs'],
    }),

    updatePOI: builder.mutation<
      POIMutationResponse,
      { poi_id: string; data: FormData; harborId?: string | null }
    >({
      query: ({ poi_id, data, harborId }) => ({
        url: `pois/${poi_id}${harborId ? `?harborId=${harborId}` : ''}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: POIMutationResponse) => response,
      invalidatesTags: ['POIs'],
    }),

    deletePOI: builder.mutation<ApiResponse<void>, { poi_id: string; harborId?: string | null }>({
      query: ({ poi_id, harborId }) => ({
        url: `pois/${poi_id}${harborId ? `?harborId=${harborId}` : ''}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['POIs'],
    }),

    createMediaPOIId: builder.mutation<void, MediaPayload>({
      query: ({ id, media }) => {
        const formData = new FormData();
        media.forEach((file) => {
          formData.append(`media[]`, file);
        });
        return {
          url: `/pois/${id}/media`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['POIs'],
    }),

    translatePOIContent: builder.mutation<
      POITranslationResponse,
      {
        name?: string;
        category_id?: string;
        category?: string;
        description?: string;
        tags?: string[];
        source_language?: string;
        poi_id?: string;
        harborId?: string | null;
      }
    >({
      query: (data) => ({
        url: `pois/translate${data.harborId ? `?harborId=${data.harborId}` : ''}`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: POITranslationResponse) => response,
    }),
    getPOICategories: builder.query<IPOICategory[], void>({
      query: () => ({
        url: 'pois/categories',
        method: 'GET',
      }),
      transformResponse: (response: IPOICategory[]) => response,
      providesTags: ['POI_Categories'],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetPOIsQuery,
  useGetPOIByIdQuery,
  useCreatePOIMutation,
  useUpdatePOIMutation,
  useDeletePOIMutation,
  useTranslatePOIContentMutation,
  useGetPOICategoriesQuery,

  useCreateMediaPOIIdMutation,
} = POIManagementService;
